/* eslint-disable react/require-default-props */
import L from 'leaflet';
import * as turf from '@turf/turf';
import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Form, Slider, Tooltip } from 'antd';
import { MapContainer, Polygon, Polyline, Marker, TileLayer, useMap } from 'react-leaflet';
import { faCheck, faCompressAlt, faFloppyDisk, faRedo, faRulerHorizontal, faSync, faUndo, faWandMagicSparkles, faXmark } from '@fortawesome/free-solid-svg-icons'

//STYLES
import { CardDados, Container } from './styles';

//APP
import { useAccess } from '../../../../../context/useAccess';
import { geralKmlPolygon } from '../../../../../Components/gerarKml';
import { postTalhao, putTalhao } from '../../../../../services/Talhao';
import { postTalhaoCultivavel, putTalhaoCultivavel } from '../../../../../services/TalhaoSafra';
import { destroyAlert, errorAlert, loadingAlert, successAlert } from '../../../../../utils/alert'

//COMPONENT
import Input from '../../../../../Components/CustomUi/input';
import ButtonCustom from '../../../../../Components/ButtonCustom';
import { Box } from '../../../../../Components/CustomUi/box';
import { Inline } from '../../../../../Components/CustomUi/inline';
import { Warning } from '../../../../../Components/CustomUi/warning';
import { TitleH3 } from '../../../../../Components/CustomUi/titleH3';
import { ModalPopOver } from '../../../../../Components/CustomUi/modalPopOver';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';



// Casas decimais usadas para considerar dois vértices na "mesma coordenada" (~1cm)
const PRECISAO_COORDENADA = 7

// Um polígono válido precisa de no mínimo 3 vértices distintos
const MINIMO_VERTICES_POLIGONO = 3

/** Chave de comparação de coordenada — arredonda para evitar ruído de ponto flutuante */
const chaveDaCoordenada = (lat: number, lng: number) =>
    `${lat.toFixed(PRECISAO_COORDENADA)}_${lng.toFixed(PRECISAO_COORDENADA)}`

/**
 * Aqui o contorno é um anel aberto (o Leaflet fecha o polígono sozinho), então
 * o último ponto normalmente NÃO repete o primeiro — diferente do fluxo de KML.
 * Mesmo assim tratamos o caso de vir fechado, para não descartar um vértice real.
 */
const anelEstaFechado = (coords: L.LatLngTuple[]) => {
    if (coords.length < 2) return false
    const primeiro = coords[0]
    const ultimo = coords[coords.length - 1]
    return chaveDaCoordenada(primeiro[0], primeiro[1]) === chaveDaCoordenada(ultimo[0], ultimo[1])
}

/** Conta vértices desconsiderando um eventual ponto de fechamento do anel */
const contarVerticesReais = (coords: L.LatLngTuple[]) => {
    if (coords.length < 2) return coords.length
    return anelEstaFechado(coords) ? coords.length - 1 : coords.length
}

/** Quantos vértices do anel estão exatamente nas mesmas coordenadas */
const contarVerticesDuplicadosDoAnel = (coords: L.LatLngTuple[]) => {
    const lista = anelEstaFechado(coords) ? coords.slice(0, -1) : coords
    if (lista.length <= MINIMO_VERTICES_POLIGONO) return 0

    const vistos = new Set<string>()
    let duplicados = 0

    lista.forEach(([lat, lng]) => {
        const chave = chaveDaCoordenada(lat, lng)
        if (vistos.has(chave)) duplicados += 1
        else vistos.add(chave)
    })

    return duplicados
}

/** Remove vértices que estão exatamente nas mesmas coordenadas */
const removerDuplicadosDoAnel = (coords: L.LatLngTuple[]) => {
    const fechado = anelEstaFechado(coords)
    const lista = fechado ? coords.slice(0, -1) : coords

    const vistos = new Set<string>()
    const unicos: L.LatLngTuple[] = []

    lista.forEach(([lat, lng]) => {
        const chave = chaveDaCoordenada(lat, lng)
        if (!vistos.has(chave)) {
            vistos.add(chave)
            unicos.push([lat, lng])
        }
    })

    const removidos = lista.length - unicos.length
    if (unicos.length < MINIMO_VERTICES_POLIGONO) return { coords, removidos: 0 }

    return {
        coords: fechado ? ([...unicos, [...unicos[0]] as L.LatLngTuple]) : unicos,
        removidos,
    }
}

/**
 * Reduz vértices mantendo apenas os que estão a pelo menos `distanciaMinima`
 * metros do último vértice preservado. Função pura — serve para a prévia e
 * para a aplicação.
 */
const reduzirPorDistancia = (coords: L.LatLngTuple[], distanciaMinima: number): L.LatLngTuple[] => {
    if (coords.length <= MINIMO_VERTICES_POLIGONO) return coords

    const fechado = anelEstaFechado(coords)
    const lista = fechado ? coords.slice(0, -1) : coords
    if (lista.length <= MINIMO_VERTICES_POLIGONO) return coords

    const mantidos: L.LatLngTuple[] = [lista[0]]

    for (let i = 1; i < lista.length; i++) {
        const [lat, lng] = lista[i]
        const [latRef, lngRef] = mantidos[mantidos.length - 1]

        const distancia = turf.distance(turf.point([lngRef, latRef]), turf.point([lng, lat]), {
            units: 'meters',
        })

        if (distancia >= distanciaMinima) mantidos.push([lat, lng])
    }

    if (mantidos.length < MINIMO_VERTICES_POLIGONO) return coords

    // O último mantido pode ter ficado colado no primeiro ao fechar o anel
    if (mantidos.length > MINIMO_VERTICES_POLIGONO) {
        const [latIni, lngIni] = mantidos[0]
        const [latFim, lngFim] = mantidos[mantidos.length - 1]
        const distanciaFechamento = turf.distance(
            turf.point([lngIni, latIni]),
            turf.point([lngFim, latFim]),
            { units: 'meters' }
        )
        if (distanciaFechamento < distanciaMinima) mantidos.pop()
    }

    return fechado ? [...mantidos, [...mantidos[0]] as L.LatLngTuple] : mantidos
}

/**
 * Simplifica apenas trechos retos (Douglas-Peucker). O critério é o desvio
 * perpendicular: uma reta com 50 vértices colapsa para 2, enquanto cantos e
 * curvas são preservados.
 */
const reduzirRetas = (coords: L.LatLngTuple[], toleranciaMetros: number): L.LatLngTuple[] => {
    if (coords.length <= MINIMO_VERTICES_POLIGONO) return coords

    const fechado = anelEstaFechado(coords)
    const anel: L.LatLngTuple[] = fechado ? coords : [...coords, [...coords[0]] as L.LatLngTuple]
    if (anel.length < 4) return coords

    // A tolerância do turf.simplify é em graus — convertemos a partir dos metros
    const grausPorMetro = 1 / 111320
    const tolerancia = toleranciaMetros * grausPorMetro

    try {
        /**
         * Simplificamos como LineString, e não como Polygon: turf.simplify lança
         * "invalid polygon" quando o anel cairia abaixo de 4 coordenadas, e não
         * simplifica anéis auto-interseccionados — justamente os contornos que
         * este módulo precisa corrigir.
         */
        const linha = turf.lineString(anel.map(([lat, lng]) => [lng, lat]))
        const simplificada = turf.simplify(linha, { tolerance: tolerancia, highQuality: true })

        const resultado = simplificada.geometry.coordinates.map(
            ([lng, lat]) => [lat, lng] as L.LatLngTuple
        )

        if (contarVerticesReais(resultado) < MINIMO_VERTICES_POLIGONO) return coords

        // Garante que o anel continue fechado após a simplificação
        if (!anelEstaFechado(resultado)) resultado.push([...resultado[0]] as L.LatLngTuple)

        return fechado ? resultado : resultado.slice(0, -1)
    } catch {
        return coords
    }
}

// Acima desta variação de área a correção é considerada arriscada e não é aplicada
const LIMITE_VARIACAO_AREA = 5

// Teto de passadas da correção — trava de segurança, o loop para sozinho ao zerar
const MAX_PASSOS_CORRECAO = 10

/** Converte o anel do componente em um polígono turf válido para medição */
const paraPoligonoTurf = (coords: L.LatLngTuple[]) => {
    const anel = coords.map(([lat, lng]) => [lng, lat])
    if (!anelEstaFechado(coords)) anel.push([coords[0][1], coords[0][0]])
    return turf.polygon([anel])
}

/** Quantidade de auto-interseções (vértices sobrepostos) do contorno */
const contarKinks = (coords: L.LatLngTuple[]) => {
    try {
        if (coords.length < MINIMO_VERTICES_POLIGONO) return 0
        return turf.kinks(paraPoligonoTurf(coords)).features.length
    } catch {
        return 0
    }
}

/** Área do contorno em hectares */
const areaEmHectares = (coords: L.LatLngTuple[]) => {
    try {
        if (coords.length < MINIMO_VERTICES_POLIGONO) return 0
        return turf.area(paraPoligonoTurf(coords)) / 10000
    } catch {
        return 0
    }
}

interface CorrecaoSobreposicao {
    /** Contorno corrigido — igual ao original quando não foi possível corrigir */
    coords: L.LatLngTuple[]
    /** Se a correção zerou as sobreposições */
    resolvido: boolean
    kinksAntes: number
    kinksDepois: number
    /** Vértices com coordenadas iguais existentes antes da correção */
    duplicadosAntes: number
    /** Quantos vértices repetidos a correção removeu (etapa 1) */
    duplicadosRemovidos: number
    /**
     * Se a etapa 2 (unkinkPolygon) precisou rodar. Só nesse caso o formato do
     * talhão muda — remover vértices repetidos não move nenhum vértice.
     */
    formatoAlterado: boolean
    areaAntes: number
    areaDepois: number
    /** Variação de área em % — o número que indica o quanto o talhão mudou */
    variacaoArea: number
    /** Área descartada ao manter apenas a maior parte, em % do total */
    descartado: number
    /** Correção possível, mas fora do limite seguro de variação de área */
    arriscado: boolean
}

/**
 * Corrige o contorno com o mínimo de alteração possível.
 *
 * Estratégia em cascata, do menos ao mais invasivo:
 *   1. remover vértices com coordenadas iguais — é a causa mais comum, já que o
 *      contorno é registrado por trator e uma máquina parada grava a mesma
 *      coordenada várias vezes. Não move nenhum vértice e não altera a área;
 *   2. turf.unkinkPolygon, só se ainda sobrar sobreposição — divide o anel no
 *      ponto de cruzamento e mantém a maior parte. Preserva as coordenadas
 *      exatas (não desloca vértices nem infla a contagem).
 *
 * A função é pura: serve para a prévia e para a aplicação.
 */
const corrigirSobreposicoes = (coords: L.LatLngTuple[]): CorrecaoSobreposicao => {
    const kinksAntes = contarKinks(coords)
    const areaAntes = areaEmHectares(coords)
    const duplicadosAntes = contarVerticesDuplicadosDoAnel(coords)

    const semCorrecao: CorrecaoSobreposicao = {
        coords,
        resolvido: kinksAntes === 0,
        kinksAntes,
        kinksDepois: kinksAntes,
        duplicadosAntes,
        duplicadosRemovidos: 0,
        formatoAlterado: false,
        areaAntes,
        areaDepois: areaAntes,
        variacaoArea: 0,
        descartado: 0,
        arriscado: false,
    }

    // Nada a fazer: sem sobreposição e sem vértices repetidos
    if (kinksAntes === 0 && duplicadosAntes === 0) return semCorrecao
    if (coords.length <= MINIMO_VERTICES_POLIGONO) return semCorrecao

    const montarResultado = (
        bruto: L.LatLngTuple[],
        descartado: number,
        duplicadosRemovidos: number,
        formatoAlterado: boolean
    ): CorrecaoSobreposicao => {
        // O componente trabalha com anel aberto. Devolver um anel fechado duplica o
        // ponto final e faz a detecção de sobreposição acusar um kink fantasma.
        const candidato = anelEstaFechado(bruto) ? bruto.slice(0, -1) : bruto
        const areaDepois = areaEmHectares(candidato)
        const variacaoArea =
            areaAntes > 0 ? (Math.abs(areaDepois - areaAntes) / areaAntes) * 100 : 0
        const kinksDepois = contarKinks(candidato)

        return {
            coords: candidato,
            resolvido: kinksDepois === 0,
            kinksAntes,
            kinksDepois,
            duplicadosAntes,
            duplicadosRemovidos,
            formatoAlterado,
            areaAntes,
            areaDepois,
            variacaoArea,
            descartado,
            arriscado: variacaoArea > LIMITE_VARIACAO_AREA || descartado > LIMITE_VARIACAO_AREA,
        }
    }

    // 1) Sempre limpa os vértices repetidos primeiro — passo mais barato e sem risco
    const semDuplicados = removerDuplicadosDoAnel(coords)
    let atual = semDuplicados.removidos > 0 ? semDuplicados.coords : coords
    const duplicadosRemovidos = semDuplicados.removidos

    // Resolveu só com isso: é o caso mais comum (trator parado repetindo coordenada).
    // O formato do talhão não muda, então não há o que revisar.
    if (contarKinks(atual) === 0) {
        if (duplicadosRemovidos === 0) return semCorrecao
        return montarResultado(atual, 0, duplicadosRemovidos, false)
    }

    /**
     * 2) Divide no ponto de cruzamento e mantém a maior parte.
     *
     * Em loop: um contorno com várias sobreposições pode não ser resolvido em uma
     * única passada, e parar no meio deixaria o alerta aceso mesmo "após a correção".
     * Só seguimos enquanto o número de kinks realmente diminui — assim nunca entra
     * em loop infinito nem piora o contorno.
     */
    let descartadoAcumulado = 0
    let rodouUnkink = false

    for (let passo = 0; passo < MAX_PASSOS_CORRECAO; passo++) {
        const kinksAtuais = contarKinks(atual)
        if (kinksAtuais === 0) break

        try {
            const partes = turf.unkinkPolygon(paraPoligonoTurf(atual))
            if (!partes?.features?.length) break

            const areas = partes.features.map((feature) => turf.area(feature))
            const areaTotal = areas.reduce((soma, valor) => soma + valor, 0)
            const indiceMaior = areas.indexOf(Math.max(...areas))

            const maior = partes.features[indiceMaior]
            const resultado = maior.geometry.coordinates[0].map(
                ([lng, lat]) => [lat, lng] as L.LatLngTuple
            )

            const aberto = anelEstaFechado(resultado) ? resultado.slice(0, -1) : resultado
            if (aberto.length < MINIMO_VERTICES_POLIGONO) break

            // Sem progresso: aborta para não girar em falso nem degradar o contorno
            if (contarKinks(aberto) >= kinksAtuais) break

            if (areaTotal > 0) {
                descartadoAcumulado += ((areaTotal - areas[indiceMaior]) / areaTotal) * 100
            }
            atual = aberto
            rodouUnkink = true
        } catch {
            break
        }
    }

    // Nem os duplicados nem o unkink alteraram o contorno
    if (atual === coords) return semCorrecao

    return montarResultado(atual, descartadoAcumulado, duplicadosRemovidos, rodouUnkink)
}

interface PreviaReducao {
    total: number
    aposReducao: number
    removidos: number
    porcentagem: number
}

/** Números exibidos na prévia dos dois redutores */
const calcularPrevia = (
    originais: L.LatLngTuple[],
    reduzidos: L.LatLngTuple[]
): PreviaReducao => {
    const total = contarVerticesReais(originais)
    if (total === 0) return { total: 0, aposReducao: 0, removidos: 0, porcentagem: 0 }

    const aposReducao = contarVerticesReais(reduzidos)
    const removidos = total - aposReducao

    return { total, aposReducao, removidos, porcentagem: (removidos / total) * 100 }
}

/** Legenda das cores desenhadas no mapa durante a prévia */
const LegendaDaPreviaNoMapa: React.FC<{ rotuloVertices?: string }> = ({
    rotuloVertices = 'Vértices que serão removidos',
}) => (
    <Box gap={6}>
        <label style={{ fontSize: 11, color: '#7b7b7b', fontWeight: 600 }}>Prévia no mapa</label>
        <Inline alignItems='center' gap={8}>
            <span style={{ width: 18, height: 3, background: '#00e676', borderRadius: 2 }} />
            <label style={{ fontSize: 11, color: '#7b7b7b' }}>Contorno resultante</label>
        </Inline>
        <Inline alignItems='center' gap={8}>
            <span style={{ width: 10, height: 10, background: '#ff9100', borderRadius: '50%' }} />
            <label style={{ fontSize: 11, color: '#7b7b7b' }}>{rotuloVertices}</label>
        </Inline>
    </Box>
)

/** Números da prévia + legenda das cores desenhadas no mapa */
const ResumoDaPrevia: React.FC<{ previa: PreviaReducao }> = ({ previa }) => (
    <>
        <Box gap={8}>
            <Inline justifyContent='space-between'>
                <label style={{ fontSize: 12, color: '#7b7b7b' }}>Total de vértices:</label>
                <strong style={{ fontSize: 13, color: '#505050' }}>{previa.total}</strong>
            </Inline>

            <Inline justifyContent='space-between'>
                <label style={{ fontSize: 12, color: '#7b7b7b' }}>Vértices após a redução:</label>
                <strong style={{ fontSize: 13, color: '#2b8a3e' }}>{previa.aposReducao}</strong>
            </Inline>

            <Inline justifyContent='space-between'>
                <label style={{ fontSize: 12, color: '#7b7b7b' }}>Porcentagem de redução:</label>
                <strong style={{ fontSize: 13, color: '#c94a4a' }}>
                    {previa.porcentagem.toFixed(1)}%
                </strong>
            </Inline>
        </Box>

        <LegendaDaPreviaNoMapa />
    </>
)

// Ajusta visão da camera Leaflet sem dar reset no zoom a cada alteração de estado parent
const ZoomToPolygonBounds = ({ coords }: { coords: L.LatLngTuple[] }) => {
    const map = useMap()
    const initialized = React.useRef(false)

    useEffect(() => {
        if (!initialized.current && coords && coords.length > 0) {
            try {
                const bounds = L.latLngBounds(coords)
                map.fitBounds(bounds, { maxZoom: 20 })
                initialized.current = true
            } catch (e) { }
        }
    }, [map, coords])
    return null
}

interface DesenhoTalhaoProps {
    isOpen: boolean
    update?: number
    onClose: (data: boolean) => void
    cultivavel?: boolean
    talhao: {
        nome: string
        propriedadeCod: number
        propriedadeNome: string
        fitBounds: L.LatLngBoundsExpression | null
        polygon: L.LatLngTuple[] | any[]
    }
    onSuccess?: () => void
    atualizarLista?: Function
}

const DesenhoTalhaoNovoNew: React.FC<DesenhoTalhaoProps> = ({
    isOpen,
    talhao,
    onClose,
    update = 0,
    cultivavel,
    onSuccess,
    atualizarLista,
}) => {
    const [map, setMap] = useState<L.Map | null>(null)
    const [polygonCoords, setPolygonCoords] = useState<L.LatLngTuple[]>([])

    const [visible, setVisible] = useState(false)

    const [getAreaTalhao, setGetAreaTalhao] = useState<number>(0)

    // Undo/Redo stores arrays of LatLngTuples
    const [undoPolygon, setUndoPolygon] = useState<L.LatLngTuple[][]>([])
    const [redoPolygon, setRedoPolygon] = useState<L.LatLngTuple[][]>([])
    const [kinksPoints, setKinksPoints] = useState<L.LatLngTuple[]>([])

    const [salvar, setSalvar] = useState(false)
    const [carregando_salvar, setCarregando_salvar] = useState<boolean>(false)
    const [nome, setNome] = useState<string>('')
    const [area, setArea] = useState<string>('')
    const [codPropriedade, setCodPropriedade] = useState<number>(null)

    const [hoverIndex, setHoverIndex] = useState<number | null>(null)

    // CORREÇÃO AUTOMÁTICA DE VÉRTICES
    const [openReducaoTrilhas, setOpenReducaoTrilhas] = useState<boolean>(false)
    const [distanciaTrilhas, setDistanciaTrilhas] = useState<number>(1)
    const [openReducaoRetas, setOpenReducaoRetas] = useState<boolean>(false)
    const [openCorrigirKinks, setOpenCorrigirKinks] = useState<boolean>(false)
    const [toleranciaRetas, setToleranciaRetas] = useState<number>(1)

    const [form] = Form.useForm()
    const { setAtualizarArea, atualizarArea } = useAccess()

    useEffect(() => {
        setVisible(isOpen)
    }, [isOpen])

    const onFinish = async () => {
        try {
            loadingAlert({ msg: 'Salvando...', id: 'cadastro_talhao' })
            setCarregando_salvar(true)

            // Adaptar para KML / Google formato para manter as API funcionando (O sistema usa {lat, lng} pro KML)
            const polygonFinish = polygonCoords.map((coord) => ({
                lat: coord[0],
                lng: coord[1],
            }))

            const kml = geralKmlPolygon(polygonFinish, nome)

            const cripto = btoa(kml)

            const arquivo = `data:@file/xml;base64,${cripto}`

            const dados = {
                nome: nome,
                cod_propriedade: update,
                arquivo: arquivo,
                status: 1,
                area: String(area),
            }

            if (update <= 0) {
                const response = cultivavel
                    ? await postTalhaoCultivavel(talhao.propriedadeCod, dados)
                    : await postTalhao(talhao.propriedadeCod, dados)
                if (response.status === 201) {
                    successAlert('Talhão cadastrado com sucesso!', 5000, 'cadastro_talhao')
                    onSuccess?.()
                    setAtualizarArea(!atualizarArea)
                    atualizarLista?.()
                    setSalvar(false)
                    setCarregando_salvar(false)
                }
            } else {
                const response = cultivavel
                    ? await putTalhaoCultivavel(talhao.propriedadeCod, update, dados)
                    : await putTalhao(talhao.propriedadeCod, update, dados)
                if (response.status === 200) {
                    successAlert('Talhão atualizado com sucesso!', 5000, 'cadastro_talhao')
                    onSuccess?.()
                    setAtualizarArea(!atualizarArea)
                    atualizarLista?.()
                    setSalvar(false)
                    setCarregando_salvar(false)
                }
                setCarregando_salvar(false)
            }
        } catch (error) {
            setCarregando_salvar(false)

            if (error.response) {
                const { data } = error.response
                if (!data.error[0].field) {
                    errorAlert(data?.error ?? 'Falha ao registrar talhão!', 5000, 'cadastro_talhao')
                } else {
                    data.error.forEach((data) => {
                        errorAlert(data?.msg?.[0] ?? 'Falha ao registrar talhão!', 5000, 'cadastro_talhao')
                    })
                }
            } else {
                destroyAlert()
            }
        }
    }

    const normalizeLatLng = (data: any[]): L.LatLngTuple[] => {
        if (!data || data.length === 0) return []
        return data.map((pt) => {
            // Se já for array [lat, lng]
            if (Array.isArray(pt)) return [pt[0], pt[1]]
            // Se for objeto API do Google {lat, lng} com funções (ou literais)
            const lat = typeof pt.lat === 'function' ? pt.lat() : pt.lat
            const lng = typeof pt.lng === 'function' ? pt.lng() : pt.lng
            return [lat, lng]
        })
    }

    // Novo calculo de área manual com Turf
    const calculateAreaGeo = (coords: L.LatLngTuple[]) => {
        if (coords.length < 3) return 0
        // O turf area precisa que o anel esteja fechado (primeiro ponto igual o último)
        // E o formato turf de polygon é [lng, lat]
        const ring = coords.map((c) => [c[1], c[0]])
        ring.push([coords[0][1], coords[0][0]]) // fechar anel

        try {
            const poly = turf.polygon([ring])
            const sqMeters = turf.area(poly)
            const ha = sqMeters / 10000
            return parseFloat(ha.toFixed(2))
        } catch {
            return 0
        }
    }

    const handleUpdatePolygonAction = (newCoords: L.LatLngTuple[]) => {
        setPolygonCoords(newCoords)
        const ha = calculateAreaGeo(newCoords)

        setGetAreaTalhao(ha)
        form.setFieldsValue({ area: ha })
        setArea(String(ha))

        setKinksPoints(validateKinks(newCoords))
    }

    const pushUndoState = (newCoords: L.LatLngTuple[]) => {
        setUndoPolygon(prev => [...prev, [...polygonCoords]])
        setRedoPolygon([]) // Limpa o redo quando uma nova ação é tomada na mão
        handleUpdatePolygonAction([...newCoords])
        setSalvar(true)
    }

    const handleUndo = () => {
        if (undoPolygon.length === 0) return
        const historyState = [...undoPolygon]
        const previous = historyState.pop()!

        // Save current to redo queue
        setRedoPolygon(prev => [...prev, [...polygonCoords]])

        setUndoPolygon(historyState)
        handleUpdatePolygonAction(previous)
    }

    const handleRedo = () => {
        if (redoPolygon.length === 0) return
        const historyState = [...redoPolygon]
        const future = historyState.pop()!

        // Save current to undo queue
        setUndoPolygon(prev => [...prev, [...polygonCoords]])

        setRedoPolygon(historyState)
        handleUpdatePolygonAction(future)
    }

    const handleResetDesenho = () => {
        setUndoPolygon([])
        setRedoPolygon([])
        const initialPolygon = normalizeLatLng(talhao.polygon)
        handleUpdatePolygonAction(initialPolygon)
        destroyAlert()
    }

    const validateKinks = (coordinates: L.LatLngTuple[]) => {
        try {
            if (coordinates.length < 4) return []
            const coords = coordinates.map((c) => [c[1], c[0]]) // Leaflet latLng -> Turf lngLat
            // Só fecha o anel se ainda não estiver fechado: duplicar o ponto final cria
            // um segmento degenerado que o turf.kinks conta como sobreposição fantasma
            if (!anelEstaFechado(coordinates)) coords.push(coords[0])
            const poly = turf.polygon([coords])
            const intersectKinks = turf.kinks(poly)
            if (intersectKinks.features.length > 0) {
                return intersectKinks.features.map(feature => {
                    return [feature.geometry.coordinates[1], feature.geometry.coordinates[0]] as L.LatLngTuple
                })
            }
            return []
        } catch {
            return []
        }
    }

    useEffect(() => {
        if (isOpen) {
            if (update > 0) {
                setNome(talhao.nome)

                const leafPolygon = normalizeLatLng(talhao.polygon)
                const ha = calculateAreaGeo(leafPolygon)

                setArea(String(ha))
                setCodPropriedade(talhao.propriedadeCod)
                setPolygonCoords(leafPolygon)
                setKinksPoints(validateKinks(leafPolygon))
            } else {
                form.resetFields()
                setNome('')
                setArea('')
                setPolygonCoords([])
            }
            setUndoPolygon([])
            setRedoPolygon([])
            setOpenReducaoTrilhas(false)
            setOpenReducaoRetas(false)
            setOpenCorrigirKinks(false)
        }
    }, [isOpen, update]) // Independente do objeto 'talhao' referenciar diferente a cada render do pai, ele trava a edição.

    const kinkIcon = L.divIcon({
        className: 'custom-kink-icon',
        html: `
      <div style="filter: drop-shadow(2px 2px 3px rgba(0,0,0,0.5)); transform: translate(-2px, -2px)">
        <svg viewBox="0 0 24 36" width="24" height="36" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.373 2 2 6.373 2 12c0 7.5 10 22 10 22s10-14.5 10-22C22 6.373 17.627 2 12 2z" fill="#ff0000" stroke="#000" stroke-width="2"/>
          <text x="12" y="16" fill="white" font-size="15" font-family="Arial, sans-serif" font-weight="bold" text-anchor="middle" dominant-baseline="middle">!</text>
        </svg>
      </div>`,
        iconSize: [24, 36],
        iconAnchor: [12, 34],
        popupAnchor: [0, -34]
    })

    const polygonRef = React.useRef<L.Polygon>(null)

    // ==========================================
    // LÓGICA DE EDIÇÃO VERTICAL MANUAl (Puxar vertices)
    // ==========================================
    const handleVertexDrag = (index: number, newPos: L.LatLngTuple) => {
        const updated = [...polygonCoords]
        updated[index] = newPos
        pushUndoState(updated)
    }

    const handleVertexRightClick = (index: number, e: L.LeafletMouseEvent) => {
        L.DomEvent.preventDefault(e.originalEvent) // BLOQUEIA O MENU DIREITO NATIVO DA TELA
        L.DomEvent.stopPropagation(e.originalEvent)

        if (polygonCoords.length <= 3) {
            errorAlert('Não é possível ter menos de 3 vértices no talhão.', 5000, 'vertex_error')
            return
        }
        const updated = [...polygonCoords]
        updated.splice(index, 1)
        pushUndoState(updated)
    }

    const handleEdgeDragCreate = (indexA: number, indexB: number, newPos: L.LatLngTuple) => {
        const updated = [...polygonCoords]
        updated.splice(indexB, 0, newPos)
        pushUndoState(updated)
    }

    // ==========================================
    // CORREÇÃO AUTOMÁTICA DE VÉRTICES
    // ==========================================

    const previaReducaoTrilhas = useMemo(
        () => calcularPrevia(polygonCoords, reduzirPorDistancia(polygonCoords, distanciaTrilhas)),
        [polygonCoords, distanciaTrilhas]
    )

    const previaReducaoRetas = useMemo(
        () => calcularPrevia(polygonCoords, reduzirRetas(polygonCoords, toleranciaRetas)),
        [polygonCoords, toleranciaRetas]
    )

    // Correção do contorno atual (usada na prévia e ao aplicar)
    const correcaoSobreposicoes = useMemo(
        () => corrigirSobreposicoes(polygonCoords),
        [polygonCoords]
    )

    // Há sobreposição ou vértice repetido a tratar
    const temAlgoParaCorrigir =
        correcaoSobreposicoes.kinksAntes > 0 || correcaoSobreposicoes.duplicadosAntes > 0

    /**
     * Geometria da prévia desenhada no mapa enquanto um popover está aberto:
     * o contorno resultante e os vértices que serão descartados.
     */
    const previewNoMapa = useMemo(() => {
        const vazio = { contorno: [] as L.LatLngTuple[], descartados: [] as L.LatLngTuple[] }

        if (!openReducaoRetas && !openReducaoTrilhas && !openCorrigirKinks) return vazio
        if (polygonCoords.length <= MINIMO_VERTICES_POLIGONO) return vazio

        let reduzido: L.LatLngTuple[]
        if (openCorrigirKinks) reduzido = correcaoSobreposicoes.coords
        else if (openReducaoRetas) reduzido = reduzirRetas(polygonCoords, toleranciaRetas)
        else reduzido = reduzirPorDistancia(polygonCoords, distanciaTrilhas)

        // Nada a mostrar quando a correção não alterou o contorno
        if (reduzido === polygonCoords) return vazio

        const mantidos = new Set(reduzido.map(([lat, lng]) => chaveDaCoordenada(lat, lng)))
        const descartados = polygonCoords.filter(
            ([lat, lng]) => !mantidos.has(chaveDaCoordenada(lat, lng))
        )

        return { contorno: reduzido, descartados }
    }, [
        polygonCoords,
        openReducaoRetas,
        openReducaoTrilhas,
        openCorrigirKinks,
        toleranciaRetas,
        distanciaTrilhas,
        correcaoSobreposicoes,
    ])

    const handleAplicarReducaoTrilhas = () => {
        if (polygonCoords.length <= MINIMO_VERTICES_POLIGONO) {
            errorAlert('Não é possível reduzir: o talhão precisa ter no mínimo 3 vértices.', 5000)
            return
        }

        const reduzido = reduzirPorDistancia(polygonCoords, distanciaTrilhas)
        const removidos = polygonCoords.length - reduzido.length

        if (removidos <= 0) {
            errorAlert(
                `Nenhum vértice foi removido com o intervalo de ${distanciaTrilhas}m. Tente um valor maior.`,
                5000
            )
            return
        }

        setOpenReducaoTrilhas(false)
        pushUndoState(reduzido)
        successAlert(`${removidos} vértices removidos pela redução de trilhas.`, 5000)
    }

    const handleAplicarReducaoRetas = () => {
        if (polygonCoords.length <= MINIMO_VERTICES_POLIGONO) {
            errorAlert('Não é possível simplificar: o talhão precisa ter no mínimo 3 vértices.', 5000)
            return
        }

        const reduzido = reduzirRetas(polygonCoords, toleranciaRetas)
        const removidos = polygonCoords.length - reduzido.length

        if (removidos <= 0) {
            errorAlert(
                `Nenhum trecho reto foi encontrado com a tolerância de ${toleranciaRetas}m. Tente um valor maior.`,
                5000
            )
            return
        }

        setOpenReducaoRetas(false)
        pushUndoState(reduzido)
        successAlert(`${removidos} vértices removidos de trechos retos.`, 5000)
    }

    const handleCorrigirSobreposicoes = () => {
        const {
            coords,
            resolvido,
            arriscado,
            variacaoArea,
            duplicadosRemovidos,
            formatoAlterado,
            kinksAntes,
            kinksDepois,
        } = correcaoSobreposicoes

        if (!temAlgoParaCorrigir) {
            errorAlert('Nenhuma sobreposição ou vértice repetido encontrado.', 5000)
            return
        }

        // Correção possível, mas alteraria demais o talhão — decisão fica com o usuário
        if (arriscado) {
            errorAlert(
                `Correção não aplicada: alteraria ${variacaoArea.toFixed(1)}% da área do talhão. Ajuste os vértices manualmente.`,
                6000
            )
            return
        }

        if (coords === polygonCoords) {
            errorAlert(
                'Não foi possível corrigir automaticamente. Ajuste os vértices manualmente.',
                5000
            )
            return
        }

        setOpenCorrigirKinks(false)
        pushUndoState(coords)

        // Detalha o que cada etapa resolveu — duplicados são a causa mais comum
        const partes: string[] = []
        if (duplicadosRemovidos > 0) {
            partes.push(`${duplicadosRemovidos} vértice(s) repetido(s) removido(s)`)
        }
        // Só creditamos a correção de sobreposição quando a etapa 2 realmente rodou:
        // vértices repetidos por si já acusam kink, e removê-los resolve sem ajustar nada
        if (kinksAntes > 0 && formatoAlterado) {
            partes.push(
                resolvido
                    ? `${kinksAntes} sobreposição(ões) corrigida(s)`
                    : `${kinksAntes - kinksDepois} de ${kinksAntes} sobreposição(ões) corrigida(s)`
            )
        }

        const resumo = partes.join(' e ')

        if (!resolvido) {
            // Corrigiu em parte: o alerta continua aceso e o usuário precisa saber
            errorAlert(
                `${resumo}. Ainda restam ${kinksDepois} sobreposição(ões) — ajuste os vértices manualmente.`,
                7000
            )
            return
        }

        /**
         * O aviso de revisar só faz sentido se o formato do talhão mudou, ou seja
         * se a etapa 2 rodou. Remover vértices repetidos não move nenhum vértice
         * nem altera a área — não há nada para revisar.
         */
        successAlert(
            formatoAlterado
                ? `${resumo}. Revise o contorno antes de salvar.`
                : `Correção automática aplicada: ${resumo}.`,
            6000
        )
    }

    // Custom Leaflet Icons for Points -> Emulating Google Maps Dots
    const vertexIcon = L.divIcon({
        className: 'custom-vertex-icon',
        html: `<div style="width:12px; height:12px; background:white; border:2px solid ${kinksPoints.length > 0 ? '#ff4d4f' : 'cyan'}; border-radius:50%; box-shadow: 0 0 2px rgba(0,0,0,0.5);"></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6]
    })

    // Vértices que serão descartados pela prévia da redução
    const descartadoIcon = L.divIcon({
        className: 'custom-descartado-icon',
        html: `<div style="width:10px; height:10px; background:#ff9100; border:1px solid #7a3f00; border-radius:50%;"></div>`,
        iconSize: [10, 10],
        iconAnchor: [5, 5]
    })

    // Meio da linha invisíveis/fantasmas (para dar pra puxar livremente)
    const edgeIcon = L.divIcon({
        className: 'custom-edge-icon',
        html: `<div style="width:10px; height:10px; background:rgba(255,255,255,0.7); border:1px solid ${kinksPoints.length > 0 ? '#ff4d4f' : 'cyan'}; border-radius:50%;"></div>`,
        iconSize: [10, 10],
        iconAnchor: [5, 5]
    })


    return (
        <Container>
            <Drawer
                open={visible}
                closeIcon={false}
                headerStyle={{ display: 'none' }}
                bodyStyle={{ padding: 0 }}
                onClose={() => onClose(false)}
                style={{ position: 'absolute' }}
                placement='bottom'
                height='100%'
            >
                <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
                    <MapContainer
                        style={{ width: '100%', height: '100%', zIndex: 1 }}
                        zoom={15}
                        center={[-15, -50]} // O MapContainer não lida bem com ref dinamico de arrays sem renderizar forçado
                        maxZoom={30} // Zoom infinito liberado localmente
                    >
                        <TileLayer
                            url='http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}'
                            maxNativeZoom={20} // Onde a foto termina, ele inicia o upscale local!
                            maxZoom={30}
                            subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                        />

                        <ZoomToPolygonBounds key={`zoom-${update}`} coords={polygonCoords} />

                        {/* RENDER POLÍGONO BASE */}
                        {polygonCoords.length > 0 && (
                            <Polygon
                                ref={polygonRef}
                                positions={polygonCoords}
                                pathOptions={{
                                    color: kinksPoints.length > 0 ? '#ff4d4f' : 'cyan', // Google Maps tem o line color default em vermelho tambem
                                    fillColor: kinksPoints.length > 0 ? '#ff4d4f' : 'cyan',
                                    weight: 3,
                                    fillOpacity: 0.35
                                }}
                            />
                        )}

                        {/* RENDER EDITORES (NÓS) DO POLÍGONO */}
                        {polygonCoords.map((coord, i) => (
                            <React.Fragment key={`frag_vertex_${i}`}>
                                {/* Vértice Real do Polígono */}
                                <Marker
                                    position={coord}
                                    icon={vertexIcon}
                                    draggable={true}
                                    eventHandlers={{
                                        drag: (e: any) => {
                                            if (polygonRef.current) {
                                                const position = e.target.getLatLng()
                                                const newCoords = [...polygonCoords]
                                                newCoords[i] = [position.lat, position.lng]
                                                polygonRef.current.setLatLngs(newCoords)
                                            }
                                        },
                                        dragend: (e) => {
                                            const marker = e.target
                                            const position = marker.getLatLng()
                                            handleVertexDrag(i, [position.lat, position.lng])
                                        },
                                        contextmenu: (e) => handleVertexRightClick(i, e) // Clique direito para excluir
                                    }}
                                />

                                {/* Meio da Linha (Fantasma) p/ Criar novos pontos */}
                                {(() => {
                                    const nextIndex = i === polygonCoords.length - 1 ? 0 : i + 1;
                                    const nextCoord = polygonCoords[nextIndex];

                                    const edgeCenter: L.LatLngTuple = [
                                        (coord[0] + nextCoord[0]) / 2,
                                        (coord[1] + nextCoord[1]) / 2
                                    ]

                                    return (
                                        <Marker
                                            position={edgeCenter}
                                            icon={edgeIcon}
                                            draggable={true}
                                            eventHandlers={{
                                                dragstart: (e: any) => {
                                                    if (polygonRef.current) {
                                                        const position = e.target.getLatLng()
                                                        const newCoords = [...polygonCoords]
                                                        newCoords.splice(nextIndex, 0, [position.lat, position.lng])
                                                        polygonRef.current.setLatLngs(newCoords)
                                                    }
                                                },
                                                drag: (e: any) => {
                                                    if (polygonRef.current) {
                                                        const position = e.target.getLatLng()
                                                        const newCoords = [...polygonCoords]
                                                        newCoords.splice(nextIndex, 0, [position.lat, position.lng])
                                                        polygonRef.current.setLatLngs(newCoords)
                                                    }
                                                },
                                                dragend: (e) => {
                                                    const marker = e.target
                                                    const position = marker.getLatLng()
                                                    handleEdgeDragCreate(i, nextIndex, [position.lat, position.lng])
                                                }
                                            }}
                                        />
                                    )
                                })()}

                            </React.Fragment>
                        ))}

                        {/* PRÉVIA DA REDUÇÃO — contorno resultante em verde */}
                        {previewNoMapa.contorno.length > 0 && (
                            <Polyline
                                positions={[...previewNoMapa.contorno, previewNoMapa.contorno[0]]}
                                interactive={false}
                                pathOptions={{ color: '#00e676', weight: 3, fillOpacity: 0 }}
                            />
                        )}

                        {/* PRÉVIA DA REDUÇÃO — vértices que serão removidos */}
                        {previewNoMapa.descartados.map((point, index) => (
                            <Marker
                                key={`descartado_${index}`}
                                position={point}
                                icon={descartadoIcon}
                                interactive={false}
                            />
                        ))}

                        {kinksPoints.map((point, index) => (
                            <Marker
                                key={`kink_${index}`}
                                position={point}
                                icon={kinkIcon}
                                interactive={false} // Click-through so it doesn't block the polygon underneath
                            />
                        ))}

                        <CardDados style={{ zIndex: 1000, pointerEvents: 'auto' }}>
                            <div style={{ display: 'flex', marginTop: '10px' }}>
                                <ButtonCustom
                                    onClick={() => onClose(false)}
                                    type='tertiary'
                                    danger
                                    icon={<FontAwesomeIcon icon={faXmark} />}
                                >
                                    Fechar
                                </ButtonCustom>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    boxSizing: 'border-box',
                                    padding: '15px',
                                    gap: '10px',
                                }}
                            >
                                <TitleH3>Dados talhão</TitleH3>
                                <Input
                                    label='Nome'
                                    placeholder='Digite o nome do talhão'
                                    value={nome}
                                    onChange={setNome}
                                />
                                <Input
                                    label='Área(ha)'
                                    placeholder='Digite a área do talhão'
                                    value={area}
                                    onChange={setArea}
                                />

                                <Box gap={8}>
                                    <label style={{ fontSize: 12, color: '#7b7b7b', fontWeight: 600 }}>
                                        Correção automática
                                    </label>

                                    <Tooltip placement='left' title={`Corrigir automaticamente linhas sobrepostas e vértices duplicados (${temAlgoParaCorrigir ? `encontrado ${correcaoSobreposicoes.kinksAntes} sobreposições e ${correcaoSobreposicoes.duplicadosAntes} vértices repetidos` : 'Nenhuma sobreposição ou vértice repetido nesse contorno'})`}><span>
                                        <ButtonCustom
                                            onClick={() => {
                                                setOpenReducaoTrilhas(false)
                                                setOpenReducaoRetas(false)
                                                setOpenCorrigirKinks(!openCorrigirKinks)
                                            }}
                                            type={openCorrigirKinks ? 'primary' : 'secondary'}
                                            icon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                                            disabled={!temAlgoParaCorrigir}
                                            style={{ width: '100%', justifyContent: 'center' }}
                                        >
                                            Corrigir automaticamente
                                        </ButtonCustom>
                                    </span></Tooltip>

                                    <Tooltip placement='left' title='Reduzir a quantidade de vértices do talhão por distância'><span>
                                        <ButtonCustom
                                            onClick={() => {
                                                setOpenReducaoRetas(false)
                                                setOpenCorrigirKinks(false)
                                                setOpenReducaoTrilhas(!openReducaoTrilhas)
                                            }}
                                            type={openReducaoTrilhas ? 'primary' : 'secondary'}
                                            icon={<FontAwesomeIcon icon={faCompressAlt} />}
                                            style={{ width: '100%', justifyContent: 'center' }}
                                        >
                                            Redutor de trilhas
                                        </ButtonCustom>
                                    </span></Tooltip>

                                    <Tooltip placement='left' title='Simplificar trechos retos do talhão, preservando cantos e curvas'><span>
                                        <ButtonCustom
                                            onClick={() => {
                                                setOpenReducaoTrilhas(false)
                                                setOpenCorrigirKinks(false)
                                                setOpenReducaoRetas(!openReducaoRetas)
                                            }}
                                            type={openReducaoRetas ? 'primary' : 'secondary'}
                                            icon={<FontAwesomeIcon icon={faRulerHorizontal} />}
                                            style={{ width: '100%', justifyContent: 'center' }}
                                        >
                                            Reduzir trilhas retas
                                        </ButtonCustom>
                                    </span></Tooltip>
                                </Box>

                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', gap: '5px' }}>
                                        <ButtonCustom
                                            disabled={undoPolygon.length <= 1}
                                            onClick={handleUndo}
                                            style={{ color: 'white' }}
                                            title='Desfazer'
                                            icon={<FontAwesomeIcon icon={faUndo} />}
                                        />
                                        <ButtonCustom
                                            disabled={redoPolygon.length <= 0}
                                            onClick={handleRedo}
                                            style={{ color: 'white' }}
                                            title='Refazer'
                                            icon={<FontAwesomeIcon icon={faRedo} />}
                                        />
                                        <ButtonCustom
                                            onClick={handleResetDesenho}
                                            style={{ color: 'white' }}
                                            type='primary'
                                            danger
                                            title='Resetar'
                                            icon={<FontAwesomeIcon icon={faSync} />}
                                        />
                                    </div>
                                    {kinksPoints.length > 0 ? (
                                        <ButtonCustom icon={<FontAwesomeIcon icon={faFloppyDisk} />} type="primary" danger disabled onClick={() => { }}>
                                            Salvar correção
                                        </ButtonCustom>
                                    ) : (
                                        <ButtonCustom
                                            icon={<FontAwesomeIcon icon={faFloppyDisk} />}
                                            onClick={onFinish}
                                            loading={carregando_salvar}
                                        >
                                            {update > 0 ? 'Atualizar talhão' : 'Gerar talhão'}
                                        </ButtonCustom>
                                    )}
                                </div>
                            </div>
                        </CardDados>
                    </MapContainer>
                </div>
            </Drawer>

            <ModalPopOver
                title='Corrigir automaticamente'
                open={visible && openCorrigirKinks}
                onClose={() => setOpenCorrigirKinks(false)}
                width={330}
                height='auto'
                top={10}
                right={375}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    {correcaoSobreposicoes.formatoAlterado ? (
                        <Warning style={{ fontSize: 11 }} type='warning'>
                            Remover os vértices repetidos não resolveu tudo, então o contorno também
                            será ajustado. Como o formato do talhão muda, revise o contorno e a área
                            antes de salvar — se não ficar correto, use o desfazer.
                        </Warning>
                    ) : (
                        <Warning style={{ fontSize: 11 }} type='info'>
                            Remove os vértices que estão na mesma coordenada. Não altera o formato
                            nem a área do talhão.
                        </Warning>
                    )}

                    <Box gap={8}>
                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Vértices repetidos:</label>
                            <strong
                                style={{
                                    fontSize: 13,
                                    color: correcaoSobreposicoes.duplicadosAntes > 0 ? '#c94a4a' : '#505050',
                                }}
                            >
                                {correcaoSobreposicoes.duplicadosAntes}
                            </strong>
                        </Inline>

                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Sobreposições:</label>
                            <strong
                                style={{
                                    fontSize: 13,
                                    color: correcaoSobreposicoes.kinksAntes > 0 ? '#c94a4a' : '#505050',
                                }}
                            >
                                {correcaoSobreposicoes.kinksAntes}
                            </strong>
                        </Inline>

                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Após a correção:</label>
                            <strong
                                style={{
                                    fontSize: 13,
                                    color: correcaoSobreposicoes.kinksDepois === 0 ? '#2b8a3e' : '#c94a4a',
                                }}
                            >
                                {correcaoSobreposicoes.kinksDepois} sobreposição(ões)
                            </strong>
                        </Inline>
                    </Box>

                    <Box gap={8}>
                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Área atual:</label>
                            <strong style={{ fontSize: 13, color: '#505050' }}>
                                {correcaoSobreposicoes.areaAntes.toFixed(2)} ha
                            </strong>
                        </Inline>

                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Área corrigida:</label>
                            <strong style={{ fontSize: 13, color: '#2b8a3e' }}>
                                {correcaoSobreposicoes.areaDepois.toFixed(2)} ha
                            </strong>
                        </Inline>

                        <Inline justifyContent='space-between'>
                            <label style={{ fontSize: 12, color: '#7b7b7b' }}>Variação da área:</label>
                            <strong
                                style={{
                                    fontSize: 13,
                                    color: correcaoSobreposicoes.arriscado ? '#c94a4a' : '#505050',
                                }}
                            >
                                {correcaoSobreposicoes.variacaoArea.toFixed(2)}%
                            </strong>
                        </Inline>
                    </Box>

                    {correcaoSobreposicoes.arriscado && (
                        <Warning style={{ fontSize: 11 }} type='error'>
                            A correção mudaria {correcaoSobreposicoes.variacaoArea.toFixed(1)}% da
                            área — acima do limite seguro de {LIMITE_VARIACAO_AREA}%. Provavelmente o
                            contorno tem um problema que precisa de ajuste manual.
                        </Warning>
                    )}

                    {!correcaoSobreposicoes.arriscado && !correcaoSobreposicoes.resolvido && (
                        <Warning style={{ fontSize: 11 }} type='error'>
                            Não foi possível resolver todas as sobreposições automaticamente.
                            Restariam {correcaoSobreposicoes.kinksDepois} — arraste os vértices para
                            separar as linhas sobrepostas.
                        </Warning>
                    )}

                    <LegendaDaPreviaNoMapa rotuloVertices='Vértices alterados pela correção' />

                    <ButtonCustom
                        onClick={handleCorrigirSobreposicoes}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        disabled={!temAlgoParaCorrigir || correcaoSobreposicoes.arriscado}
                        title={
                            temAlgoParaCorrigir && !correcaoSobreposicoes.arriscado
                                ? 'Aplicar a correção no contorno'
                                : 'Correção automática indisponível para este contorno'
                        }
                        style={{ width: '100%', justifyContent: 'center' }}
                    >
                        Confirmar correção
                    </ButtonCustom>

                </div>
            </ModalPopOver>

            <ModalPopOver
                title='Redutor de trilhas'
                open={visible && openReducaoTrilhas}
                onClose={() => setOpenReducaoTrilhas(false)}
                width={330}
                height='auto'
                top={10}
                right={375}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    <Warning style={{ fontSize: 11 }} type='info'>
                        Vértices que estiverem a menos da distância informada do vértice anterior serão
                        removidos. Confira a prévia no mapa antes de confirmar.
                    </Warning>

                    <div>
                        <label style={{ fontSize: 12, color: '#7b7b7b', fontWeight: 600 }}>
                            Distância entre os vértices:{' '}
                            <strong style={{ color: '#505050', fontSize: 13 }}>{distanciaTrilhas}m</strong>
                        </label>
                        <Slider
                            min={1}
                            max={50}
                            step={1}
                            value={distanciaTrilhas}
                            onChange={(valor: number) => setDistanciaTrilhas(valor)}
                            marks={{ 1: '1m', 25: '25m', 50: '50m' }}
                        />
                    </div>

                    <ResumoDaPrevia previa={previaReducaoTrilhas} />

                    <ButtonCustom
                        onClick={handleAplicarReducaoTrilhas}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        disabled={previaReducaoTrilhas.removidos <= 0}
                        title={
                            previaReducaoTrilhas.removidos > 0
                                ? `${previaReducaoTrilhas.removidos} vértices serão removidos`
                                : 'Nenhum vértice seria removido com esse intervalo'
                        }
                        style={{ width: '100%', justifyContent: 'center' }}
                    >
                        Confirmar redução
                    </ButtonCustom>

                </div>
            </ModalPopOver>

            <ModalPopOver
                title='Reduzir trilhas retas'
                open={visible && openReducaoRetas}
                onClose={() => setOpenReducaoRetas(false)}
                width={330}
                height='auto'
                top={10}
                right={375}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    <Warning style={{ fontSize: 11 }} type='info'>
                        Simplifica apenas os trechos retos: vértices que não alteram o formato da linha são
                        removidos, preservando cantos e curvas. Confira a prévia no mapa antes de confirmar.
                    </Warning>

                    <div>
                        <label style={{ fontSize: 12, color: '#7b7b7b', fontWeight: 600 }}>
                            Tolerância:{' '}
                            <strong style={{ color: '#505050', fontSize: 13 }}>{toleranciaRetas}m</strong>
                        </label>
                        <Slider
                            min={0.5}
                            max={5}
                            step={0.5}
                            value={toleranciaRetas}
                            onChange={(valor: number) => setToleranciaRetas(valor)}
                            marks={{ 0.5: '0,5m', 5: '5m' }}
                        />
                    </div>

                    <ResumoDaPrevia previa={previaReducaoRetas} />

                    <ButtonCustom
                        onClick={handleAplicarReducaoRetas}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        disabled={previaReducaoRetas.removidos <= 0}
                        title={
                            previaReducaoRetas.removidos > 0
                                ? `${previaReducaoRetas.removidos} vértices serão removidos`
                                : 'Nenhum trecho reto seria simplificado com essa tolerância'
                        }
                        style={{ width: '100%', justifyContent: 'center' }}
                    >
                        Confirmar redução
                    </ButtonCustom>

                </div>
            </ModalPopOver>
        </Container>
    )
}

export default DesenhoTalhaoNovoNew